import { forwardRef, type HTMLAttributes } from 'react';
import { Badge, type BadgeTone } from './Badge';
import { cn } from '../lib/cn';

/**
 * The eight things this product knows about a number, as a vocabulary rather
 * than as seventy sentences.
 *
 * ## WHY THIS EXISTS
 *
 * Wend distinguishes eight states a figure can be in. Seven of them were earned
 * by a defect; `projected` is the exception, and the list below says so against
 * its entry — a reader who believes all eight came from defects will eventually
 * wonder which defect produced that one and revert it looking for the answer.
 *
 * A screen once printed `0.00% — Under the line` in green
 * over a thousand emails nobody had reported on; another drew a month of zero
 * bounce stubs under Amazon's threshold line. The defence, every time, was a
 * sentence: *"Amazon has reported no delivery and no bounce for any of them — so
 * there is nothing for a rate to be a fraction of."* Each sentence was correct
 * and each was reviewed only against its own change, so by slice 24 the Overview
 * carried 1,489 words, one fact was printed four times in full, and the credit
 * balance — the figure a sender checks before every campaign — sat below all of
 * it.
 *
 * **This module removes no state and softens no qualification.** It changes what
 * carries them. A reader who learns "NOT MEASURED" once reads it everywhere;
 * a reader given eight paragraphs learns to skip paragraphs, which is how the
 * one that matters gets skipped too. The cause keeps every word it had — one
 * level down, in a disclosure, and on the screen that repairs it.
 *
 * ## THE EIGHT, AND THE CONTRACT FACT EACH ONE IS
 *
 * None of these is invented for the design system. Each already exists in
 * `@wend/shared`, and several are deliberately unrepresentable as anything else:
 *
 *   `measured`     a real reading over a sufficient denominator.
 *   `estimated`    `confidence: 'directional'` on the engagement contract — a
 *                  `z.literal`, not an enum, so nobody can write `'exact'`.
 *   `projected`    `basis: 'assumed_rates'` on `projectedFiguresSchema`. **The
 *                  one state here that was not earned by a defect** — it was
 *                  asked for, by the owner, three times: show assumed figures
 *                  until real ones arrive. It is also **the one state that is
 *                  not a measurement at all**, and the reason
 *                  it is not folded into `estimated`: `estimated` means a real
 *                  reading with a known bias, and one chip meaning both would
 *                  make "Estimated" ambiguous on a single page. Its figures come
 *                  from multiplying unreported messages by a stated assumption;
 *                  they vanish the moment a provider reports anything.
 *   `floor`        `confidence: 'floor'` on the WhatsApp read contract. The real
 *                  number is higher by an amount nothing can calculate.
 *   `awaiting`     `awaitingEvents > 0` — accepted, with no report back yet.
 *   `under_floor`  a denominator under `OVERVIEW_MIN_VOLUME_FOR_RATE`. Counts,
 *                  never a percentage: one bad address in forty reads as 2.5%.
 *   `not_measured` `noOutcomeReported()`, or engagement's untracked arm, whose
 *                  schema has no field a number could live in.
 *   `not_set_up`   a step is missing and waiting will not fix it —
 *                  `deliveryReports[channel].setup`, `lastReceivedAt === null`.
 *
 * ## WHAT IS STRUCTURAL HERE, RATHER THAN REMEMBERED
 *
 * **{@link NoFigure} accepts no number.** It is what stands in a figure slot
 * when there is no figure, and the whole class of defect above is a `0` rendered
 * where one of these states belongs. There is no prop to pass a `0` to. The
 * `Exclude<FigureState, 'measured' | 'projected'>` on its `state` is the other
 * half: a dash over a figure that HAS one — measured or assumed — is a compile
 * error, not a review finding.
 *
 * **The tone is fixed per state, here, once.** The same state in two colours on
 * two screens is two states as far as a reader is concerned, and every tone in
 * this package is semantic rather than decorative. Note which are `neutral`:
 * `awaiting` and `under_floor` are not faults. Nothing is wrong with a workspace
 * that sent an hour ago, and a warning tint there would teach the reader that
 * the tint means nothing.
 *
 * **`measured` is in the union and has no chip of its own.** A chip on every
 * real figure is noise, and noise is what makes the seven that matter invisible.
 * It is in the type so a `Record<FigureState, …>` elsewhere is total: a screen
 * that handles seven states stops compiling rather than rendering a blank.
 *
 * ## WHY THE WORDS ARE HERE AND THE REASONS ARE NOT
 *
 * "Not measured" is generic and belongs to the design system; *why* this
 * particular figure is not measured is product knowledge and stays in
 * `@wend/shared`, beside the contract that decides it. So `label` overrides the
 * word for the one case where the state's name is not what a person calls it —
 * "No reports yet" reads better than "Not measured" beside a delivery figure —
 * and the sentence explaining Amazon's configuration set goes in the disclosure,
 * not in here. A design system that knows what SNS is has stopped being one.
 */
export const FIGURE_STATE_VALUES = [
  'measured',
  'estimated',
  'projected',
  'floor',
  'awaiting',
  'under_floor',
  'not_measured',
  'not_set_up',
] as const;

export type FigureState = (typeof FIGURE_STATE_VALUES)[number];

/**
 * The state a figure slot can be in when it holds no figure.
 *
 * `projected` is excluded alongside `measured`, and for the same reason rather
 * than a different one. {@link NoFigure} draws an em-dash where a figure would
 * be, and both of these states describe a slot that **has** a number in it — one
 * measured, one assumed. `<NoFigure state="projected" />` would put a dash over
 * a figure the product does have, which is the mirror image of the defect the
 * `measured` exclusion exists to prevent. Review found it compiling.
 */
export type AbsentFigureState = Exclude<FigureState, 'measured' | 'projected'>;

interface FigureStateDetail {
  /** The chip's word. Overridable per site; see the header. */
  readonly label: string;
  readonly tone: BadgeTone;
  /**
   * What a screen reader hears in place of the em-dash, and the sentence a
   * card's disclosure is written from. A full clause, because it is read without
   * the figure's label beside it. Never a tooltip — see {@link StateChip}.
   */
  readonly meaning: string;
}

/**
 * Total over {@link FIGURE_STATE_VALUES}, so a new state is a compile error in
 * this file rather than an unstyled chip on a screen nobody is looking at.
 */
export const FIGURE_STATE_DETAIL: Readonly<Record<FigureState, FigureStateDetail>> = {
  measured: {
    label: 'Measured',
    tone: 'neutral',
    meaning: 'Measured.',
  },
  estimated: {
    label: 'Estimated',
    tone: 'info',
    meaning: 'An estimate — directional, not an exact count.',
  },
  projected: {
    label: 'Projected',
    tone: 'info',
    /*
     * Generic, like every other `meaning` here. The first draft ended "Nothing
     * has been reported for these messages", which is the gate condition —
     * product knowledge, in the design system, which this file's own header
     * forbids. Why a particular figure is projected belongs beside the contract
     * that decides it; `PROJECTION_ASSUMPTION_COPY` says it there, on screen.
     */
    meaning: 'A projection from an assumed rate, not a measurement.',
  },
  floor: {
    label: 'At least',
    tone: 'info',
    meaning: 'A floor, not a count. The real number is higher.',
  },
  awaiting: {
    label: 'Awaiting reports',
    tone: 'neutral',
    meaning: 'Sent, with no report back yet.',
  },
  under_floor: {
    label: 'Too few to rate',
    tone: 'neutral',
    meaning: 'Counts, not a percentage — too few reported outcomes for a rate to mean anything.',
  },
  not_measured: {
    label: 'Not measured',
    tone: 'warn',
    meaning: 'Not measured — this is an absence, not a zero.',
  },
  not_set_up: {
    label: 'Not set up',
    tone: 'warn',
    meaning: 'Not set up, so nothing can be measured. Waiting will not fix it.',
  },
};

/* -------------------------------------------------------------------------- */
/* The chip                                                                    */
/* -------------------------------------------------------------------------- */

export interface StateChipProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  readonly state: FigureState;
  /**
   * Replaces the state's word where a screen calls it something a person
   * recognises better — "No reports yet" for `not_measured` beside a delivery
   * figure. It cannot change the tone: the same state is one colour everywhere.
   */
  readonly label?: string;
}

/**
 * The state of a figure, said in one or two words beside it.
 *
 * Colour is never the only carrier — the word inside says the state too, which
 * is `Badge`'s rule and the reason this is a `Badge` rather than a tinted dot.
 *
 *     <StateChip state="not_measured" label="No reports yet" />
 *     <StateChip state="floor" />
 *
 * **It carries no tooltip, and that is deliberate.** Slice 25's own brief
 * proposed one — *"the sentence becomes that badge's tooltip"* — and this
 * codebase had already rejected that, app-wide, for reasons that are still
 * correct: a skimmer never opens a tooltip, tooltips do not exist on touch, and
 * they are not reachable by keyboard. `verify-page-hardening` bans a `title=`
 * on a span anywhere in a screen file. A state's meaning therefore goes where
 * everybody can reach it — the one disclosure on the card — and the chip carries
 * the word alone. `FIGURE_STATE_DETAIL[state].meaning` is exported for whoever
 * writes that disclosure, and for {@link NoFigure}, where there is no visible
 * word for a screen reader to fall back on.
 */
export const StateChip = forwardRef<HTMLSpanElement, StateChipProps>(function StateChip(
  { state, label, className, ...rest },
  ref,
) {
  const detail = FIGURE_STATE_DETAIL[state];

  return (
    <Badge
      {...rest}
      ref={ref}
      tone={detail.tone}
      data-figure-state={state}
      className={className}
    >
      {label ?? detail.label}
    </Badge>
  );
});

/* -------------------------------------------------------------------------- */
/* The absent figure                                                           */
/* -------------------------------------------------------------------------- */

export interface NoFigureProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  /**
   * `measured` is excluded by the type: a dash drawn over a figure that exists
   * is the inverse of the defect this component was built for, and it should
   * not compile.
   */
  readonly state: AbsentFigureState;
  /** Overrides what a screen reader hears. Defaults to the state's meaning. */
  readonly meaning?: string;
}

/**
 * What sits in a figure slot when there is no figure.
 *
 * **It takes no value, and that is the whole point.** Every rendering defect
 * this codebase has shipped on a dashboard was a `0` standing where one of the
 * absent states belonged — a confident `0.00%` over mail nobody had reported on,
 * five reasons for non-delivery at `0` each. A component with a `value` prop
 * would accept that `0`; this one has nowhere to put it.
 *
 * The dash is `aria-hidden` and the meaning is `sr-only` beside it, because a
 * screen reader meeting a bare em-dash announces anything from "dash" to
 * silence, and silence here is indistinguishable from a zero.
 */
export const NoFigure = forwardRef<HTMLSpanElement, NoFigureProps>(function NoFigure(
  { state, meaning, className, ...rest },
  ref,
) {
  const detail = FIGURE_STATE_DETAIL[state];

  return (
    <span
      {...rest}
      ref={ref}
      data-figure-state={state}
      data-role="no-figure"
      className={cn('text-muted', className)}
    >
      <span aria-hidden="true">—</span>
      <span className="sr-only">{meaning ?? detail.meaning}</span>
    </span>
  );
});
